import { FunctionComponent, useContext, useEffect, useState } from "react";
import Link from "next/link";
import { useTranslation } from "next-i18next/pages";
import AppContext from "context/AppContext";
import styles from "../../styles/common/Footer.module.css";
import { useInView } from "react-intersection-observer";

interface FooterProps {
  doAnimation?: boolean;
  hideFinalCta?: boolean;
}

const Footer: FunctionComponent<FooterProps> = ({
  doAnimation = false,
  hideFinalCta = false
}) => {
  const { isMobile } = useContext(AppContext);
  const { t, i18n } = useTranslation("common");
  const [isAnimationOn, setIsAnimationOn] = useState<boolean>(false);
  const { ref, inView } = useInView({ rootMargin: "0% 0% -30%" });
  const titleClasses = [
    styles.title,
    i18n.language === "de" ? styles.titleGerman : ""
  ]
    .filter(Boolean)
    .join(" ");

  useEffect(() => {
    if (inView) {
      setIsAnimationOn(true);
    }
  }, [inView]);

  return (
    <footer
      className={`${styles.footerContainer}
                  ${doAnimation && isAnimationOn && !isMobile ? styles.animated : ""}
                  ${doAnimation && !isMobile ? styles.doAnimation : ""}`}
      ref={ref}
    >
      {!hideFinalCta && (
        <div className={styles.finalCta}>
          <div className={styles.orbit} aria-hidden="true" />
          <div className={styles.finalInner}>
            <p className={styles.eyebrow}>{t("footer.eyebrow")}</p>
            <h2 className={titleClasses}>{t("footer.title")}</h2>
            <div className={styles.finalRow}>
              <p className={styles.kicker}>{t("footer.text")}</p>
              <Link href="/contact" className={styles.bookConsultation}>
                {t("footer.bookConsultation")} <span aria-hidden="true">↗</span>
              </Link>
            </div>
          </div>
        </div>
      )}

      <div className={styles.footerMeta}>
        <div className={styles.footerGrid}>
          <div className={styles.footerBrand}>
            <Link href="/" className={styles.brand} aria-label="Browserbite — Go to homepage">
              <span className={styles.brandLogo} aria-hidden="true" />
            </Link>
            <p>{t("global.websiteDescription")}</p>
          </div>

          <div>
            <h3>{t("navigation.all.0.name")}</h3>
            <ul>
              <li><Link href="/contact">{t("global.bookCall")}</Link></li>
              <li><Link href="/case-studies">{t("footer.caseStudies")}</Link></li>
              <li><Link href="/weitere-leistungen">{t("footer.otherServices")}</Link></li>
              <li><Link href="/ueber-uns">{t("footer.aboutUs")}</Link></li>
            </ul>
          </div>

          <div>
            <h3>{t("footer.contactHeading")}</h3>
            <ul>
              <li><a href={`mailto:${t("footer.email")}`}>{t("footer.email")}</a></li>
              <li><a href={`tel:${t("footer.telDe")}`}>{t("footer.telDe")}</a></li>
              <li>{t("footer.address")}</li>
            </ul>
          </div>

          <div>
            <h3>{t("footer.legalHeading")}</h3>
            <ul>
              <li><Link href="/impressum">{t("footer.imprint")}</Link></li>
              <li><Link href="/datenschutz">{t("footer.privacyPolicy")}</Link></li>
              <li><Link href="/accessibility">{t("footer.accessibility")}</Link></li>
              <li>
                <a href="https://www.linkedin.com/company/browserbite-ltd/" target="_blank" rel="noopener noreferrer">
                  LinkedIn ↗
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className={styles.footerBottom}>
          <span>{t("footer.rights")}</span>
          <span>{t("footer.tagline")}</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
